import React, {useState} from 'react';

import {
  PublicDoorKeyFragmentFragment,
  useDoorKeyQuery,
  useUnlockDoorKeyMutation,
} from '@/api';

// 電子キーの解錠ページ（/k/:token。ログイン不要）。ヘッダー・フッターは出さない（Rails の door_key レイアウト）。
// 時間の判定はサーバーが行う。このページは状態を出し分けるだけ（仕様書 5-4）。

export const tokenFromPath = (pathname: string) =>
  pathname.match(/^\/k\/([^/]+)/)?.[1] ?? '';

const Contact = () => (
  <div className="mt-8 border-t border-gray-200 pt-4 text-sm text-gray-600">
    <p className="font-medium text-gray-700">開けられないとき・お困りのとき</p>
    <p className="mt-1">
      ACミハラ（株式会社エムセック）
      <br />
      電話 <a href="tel:0848-63-5584">0848-63-5584</a>（平日 9:00〜17:30）
      <br />
      メール <a href="mailto:info@msec.co.jp">info@msec.co.jp</a>
    </p>
  </div>
);

const Windows = ({label, texts}: {label: string; texts: string[]}) => (
  <div className="mt-4">
    <p className="text-sm font-medium text-gray-700">{label}</p>
    <ul className="mt-1 space-y-1 text-base">
      {texts.map((text) => (
        <li key={text}>{text}</li>
      ))}
    </ul>
  </div>
);

type Notice = {kind: 'success' | 'error'; message: string};

const Body = ({
  doorKey,
  unlocking,
  onUnlock,
  onReload,
}: {
  doorKey: PublicDoorKeyFragmentFragment;
  unlocking: boolean;
  onUnlock: () => void;
  onReload: () => void;
}) => {
  const unlockTexts = doorKey.windows.map((w) => w.unlockText);
  switch (doorKey.state) {
    case 'available':
      return (
        <>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm text-gray-600">ご予約</dt>
              <dd className="text-lg font-semibold">{doorKey.guestName} 様</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-600">スペース</dt>
              <dd className="text-base">{doorKey.spaceName}</dd>
            </div>
          </dl>
          <Windows
            label="ご利用時間"
            texts={doorKey.windows.map((w) => w.useText)}
          />
          <Windows label="解錠できる時間" texts={unlockTexts} />
          <button
            type="button"
            onClick={onUnlock}
            disabled={unlocking}
            className="mt-6 w-full rounded-md bg-blue-600 p-4 text-lg font-bold text-white hover:bg-blue-700 disabled:bg-gray-400">
            {unlocking ? '解錠しています…' : '扉を開ける'}
          </button>
        </>
      );
    case 'before':
      return (
        <>
          <h2 className="text-xl font-bold">まだご利用時間ではありません</h2>
          <Windows label="解錠できる時間" texts={unlockTexts} />
          <p className="mt-4 text-sm">
            解錠できる時間になったら、「表示を更新」を押すか、このページを開き直してください。
          </p>
          <button
            type="button"
            onClick={onReload}
            className="mt-4 w-full rounded-md border border-gray-300 bg-white px-4 py-3 text-base hover:bg-gray-50">
            表示を更新
          </button>
        </>
      );
    case 'after':
      return (
        <>
          <h2 className="text-xl font-bold">ご利用時間を過ぎています</h2>
          <Windows label="解錠できた時間" texts={unlockTexts} />
          <p className="mt-4 text-sm">
            この電子キーでは、もう扉を開けられません。お忘れ物などは下の連絡先へご連絡ください。
          </p>
        </>
      );
    default:
      return (
        <>
          <h2 className="text-xl font-bold">無効なキーです</h2>
          <p className="mt-4 text-sm">
            URL が途中で切れていないか確かめてください。メールに書かれた URL
            をそのまま開いてもこの表示になるときは、下の連絡先へご連絡ください。
          </p>
        </>
      );
  }
};

export const DoorKeyPage: React.FC = () => {
  const token = tokenFromPath(window.location.pathname);
  const {data, loading, error, refetch} = useDoorKeyQuery({
    variables: {token},
    fetchPolicy: 'network-only',
  });
  const [unlockDoorKey, {loading: unlocking}] = useUnlockDoorKeyMutation();
  const [notice, setNotice] = useState<Notice | null>(null);
  const [doorKeyAfterUnlock, setDoorKeyAfterUnlock] =
    useState<PublicDoorKeyFragmentFragment | null>(null);
  const doorKey = doorKeyAfterUnlock ?? data?.doorKey;

  const handleUnlock = async () => {
    setNotice(null);
    try {
      const result = await unlockDoorKey({variables: {token}});
      const payload = result.data?.unlockDoorKey;
      if (!payload) throw new Error('no payload');
      setDoorKeyAfterUnlock(payload.doorKey);
      setNotice({
        kind: payload.result === 'unlocked' ? 'success' : 'error',
        message: payload.message,
      });
    } catch {
      setNotice({
        kind: 'error',
        message:
          '通信できませんでした。電波の届く場所で、もう一度「扉を開ける」を押してください。',
      });
    }
  };

  const handleReload = () => {
    setNotice(null);
    setDoorKeyAfterUnlock(null);
    void refetch();
  };

  return (
    <main className="mx-auto min-h-screen max-w-md bg-white px-4 py-8 text-gray-900">
      <p className="text-sm text-gray-600">ACミハラ</p>
      <h1 className="mb-6 text-2xl font-bold">電子キー</h1>
      {notice && (
        <div
          role={notice.kind === 'success' ? 'status' : 'alert'}
          className={
            notice.kind === 'success'
              ? 'mb-4 rounded-md border border-green-300 bg-green-50 p-3 text-green-900'
              : 'mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-red-900'
          }>
          {notice.message}
        </div>
      )}
      {loading && !doorKey && <p>読み込んでいます…</p>}
      {error && !doorKey && (
        <div role="alert">
          <p>
            {error.graphQLErrors[0]?.message ??
              '表示できませんでした。電波の届く場所で「表示を更新」を押してください。'}
          </p>
          <button
            type="button"
            onClick={handleReload}
            className="mt-4 w-full rounded-md border border-gray-300 bg-white px-4 py-3 text-base hover:bg-gray-50">
            表示を更新
          </button>
        </div>
      )}
      {doorKey && (
        <Body
          doorKey={doorKey}
          unlocking={unlocking}
          onUnlock={() => void handleUnlock()}
          onReload={handleReload}
        />
      )}
      <Contact />
    </main>
  );
};
